// app/javascript/controllers/async_google_image_controller.js
import { Controller } from "@hotwired/stimulus"
import consumer from "../../channels/consumer"

export default class extends Controller {
  static targets = ["carouselContainer", "template", "controls", "mapContainer", "loadingIndicator"]
  static values = { placeId: String, url: String, token: String, providerType: String, photos: Array }

  connect() {
    // Manual photo links (repurposed google_photo_url) always win: render them, skip the Google fetch.
    if (this.photosValue.length) {
      this.renderCarousel(this.photosValue, 1)
    } else {
      this.fetchData();
    }
  }

  disconnect() {
    this.removeSubscription();
  }

  removeSubscription() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  async fetchData() {
    try {
      if (!this.urlValue) return
      const response = await fetch(`${this.urlValue}?token=${this.tokenValue}&provider_type=${this.providerTypeValue}&t=${new Date().getTime()}`)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const data = await response.json()

      if (data.status === 'success') {
        this.renderCarousel(data.photos)
      } else {
        this.setupSubscription()
      }
    } catch (error) {
      // Failed to fetch -> fallback to map view
      this.renderCarousel([])
    }
  }

  renderCarousel(photos, minCount = 2) {
    // clear the loading skeleton
    if (!this.hasCarouselContainerTarget) return;
    this.removeSubscription();
    this.carouselContainerTarget.innerHTML = ''

    if (!photos || photos.length < minCount) {
      const bottomMap = document.getElementById("provider-main-map-wrapper")

      if (bottomMap && this.hasMapContainerTarget) {
        this.mapContainerTarget.appendChild(bottomMap)
        if (this.hasCarouselContainerTarget) this.carouselContainerTarget.remove()
        
        // update styles
        this.mapContainerTarget.classList.remove('hidden')
        bottomMap.classList.remove('rounded-xl', 'shadow-sm', 'h-80') 
        bottomMap.classList.add('h-full', 'w-full')

        // Moving DOM elements can confuse Google Maps, trigger a resize.
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'))
        }, 50)
      }
      return
    }

    // Update the 'total' value for the photo-carousel controller
    this.element.dataset.photoCarouselTotalValue = photos.length

    // Loop and Clone
    photos.forEach((photo, index) => {
      const clone = this.templateTarget.content.cloneNode(true)
      const rootDiv = clone.querySelector('[data-photo-carousel-target="photo"]')
      
      // Set the index required by your carousel logic
      rootDiv.dataset.index = index

      // A lone image would only fill the left half of the 2-col grid, span both.
      if (photos.length === 1) rootDiv.classList.add('sm:col-span-2')

      // Set Image
      const img = clone.querySelector('img')
      img.src = photo.url
      img.alt = `Anbieter Foto ${index + 1}`

      if (photo.attribution) {
        const attrContainer = clone.querySelector('.attribution-container')
        const attrContent = clone.querySelector('.attribution-content')
        
        attrContainer.classList.remove('hidden')
        attrContent.innerHTML = photo.attribution 
      }

      // Append to DOM
      this.carouselContainerTarget.appendChild(clone)
    })

    if (this.hasMapContainerTarget) this.mapContainerTarget.remove()
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.remove()

    // Show controls if needed
    if (photos.length > 2 && this.hasControlsTarget) {
      this.controlsTarget.classList.remove('hidden')
    }
    
    // tell the carousel
    this.element.dispatchEvent(new CustomEvent("carousel:content-changed", { bubbles: true }))
    // kill connection once done
    if (this.subscription) this.subscription.unsubscribe()
  }

  setupSubscription() {
    if (this.subscription) return

    this.subscription = consumer.subscriptions.create(
      { channel: "PlaceImageChannel", place_id: this.placeIdValue },
      {
        connected: () => {
          // Just in case we missed the broadcast while connecting
          this.fetchData()
        },
        received: (data) => {
          if (data.status === 'processed') {
            // Received signal, run the request again to hit the cache.
            this.fetchData()
          }
          else if (data.status === 'error') {
            this.renderCarousel([]);
          }
        }
      }
    )
  }
}